import * as React from "react";
import { useFormContext } from "react-hook-form";
import {
  ViewContext,
} from 'components/lib';
import { Controller } from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectGroup,
} from "../../../../components/ui/select";import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../components/ui/dialog";
import { Switch } from "../../../../components/ui/switch";
import { FieldError } from "../FieldError";
import { useWatch } from "react-hook-form";
import { Producer } from "./Producer";
import { CarrierPicker } from "./CarrierPicker";
import { AgentEmail } from "./AgentEmail";
import { PolicyEffectiveDate } from "./PolicyEffectiveDate";
import { AlertCircle } from "lucide-react";
import { normalizeProducer } from "./normalizeProducer";
import { GarageState } from "./GarageState";

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export function InitialInformation({ methods, user, producers }) {
  const { control } = useFormContext();
  const [openPolicyDate, setOpenPolicyDate] = React.useState(false);
  const context = React.useContext(ViewContext);
  const [openPersonalTrailerWarn, setOpenPersonalTrailerWarn] = React.useState(false);
  const personalTrailerSetterRef = React.useRef(() => {});

  const [openPersonalVehicleWarn, setOpenPersonalVehicleWarn] = React.useState(false);
  const personalVehicleSetterRef = React.useRef(() => {});

  const insuredWithProgressiveCommercialAuto = useWatch({ control, name: "step1.insuredWithProgressiveCommercialAuto" });
  const operatedTwoYears = useWatch({ control, name: "step1.operatedTwoYears" });
  const carrier = useWatch({ control, name: "step1.carrier" });

  React.useEffect(() => {
    if (insuredWithProgressiveCommercialAuto === 'yes' && carrier?.includes('Progressive')) {
      methods.setValue('step1.carrier', carrier.filter((c) => c !== 'Progressive'))
      methods.setValue('step1.insuredWithProgressiveCommercialAuto', 'na');
      context.notification.show(
        'We cannot run a Progressive quote for currently insured customers.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }, [insuredWithProgressiveCommercialAuto]);
  React.useEffect(() => {
    if (operatedTwoYears === 'no' && carrier?.includes('Nirvana')) {
      methods.setValue('step1.carrier', carrier.filter((c) => c !== 'Nirvana'))
      methods.setValue('step1.operatedTwoYears', 'na');
      context.notification.show(
        'We cannot run a Nirvana quote if insured has not operated under this DOT # for 2 or more FULL years.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }, [operatedTwoYears]);
  const producerOptions = React.useMemo(() => {
    if (producers.loading || !producers.data?.users) return [];
    return producers.data.users.map((u) => {
      return {
        value: String(u.id),
        label: u.name,
        email: u.email
      };
    });
  }, [producers.loading, producers.data]);

  React.useEffect(() => {
    if (user.loading || !user.data || !producerOptions.length) return;

    const current = methods.getValues("step1.producer");

    const normalized = normalizeProducer(current);

    // If already set but wrong shape, coerce once
    if (current && current !== normalized) {
      methods.setValue("step1.producer", normalized, { shouldDirty: false, shouldValidate: true });
      return;
    }

    if (!normalized) {
      const match = producerOptions.find((u) => u.value === String(user.data.id));
      if (match) {
        methods.setValue("step1.producer", normalizeProducer(match.value), { shouldDirty: false, shouldValidate: true });
      }
    }
  }, [user.loading, user.data, producerOptions, methods]);

  React.useEffect(() => {
    const d = methods.getValues("step1.policyDate");
    if (!d) {
      methods.setValue("step1.policyDate", new Date(), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    methods.register("step1.hazmatPlacard");
    methods.register("step1.haulDoubleTripleTrailers");
  }, []);

  const hazmat = useWatch({ control, name: "step1.hazmatPlacard" });
  const haulDoubleTripleTrailers = useWatch({ control, name: "step1.haulDoubleTripleTrailers" });
  const blockedCls = hazmat ? "opacity-50 pointer-events-none" : "";

  React.useEffect(() => {
    if (hazmat) {
      methods.setValue("step1.carrier", (carrier || []).filter((c) => c === "Accelerant"), { shouldDirty: true, shouldValidate: true });
    }
  }, [hazmat]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Initial Information</h2>
      <Producer
        control={control}
        producers={producers}
        producerOptions={producerOptions}
      />

      <AgentEmail control={control} producers={producers} />

      <PolicyEffectiveDate
        control={control}
        openPolicyDate={openPolicyDate}
        setOpenPolicyDate={setOpenPolicyDate}
      />

      <GarageState control={control} producers={producers} setValue={methods.setValue} />

      <CarrierPicker control={control} setValue={methods.setValue} getValues={methods.getValues} watch={methods.watch} />

      {(((insuredWithProgressiveCommercialAuto === 'na' || insuredWithProgressiveCommercialAuto === undefined) && carrier?.includes('Progressive')) || (insuredWithProgressiveCommercialAuto !== 'na' && insuredWithProgressiveCommercialAuto !== undefined)) && <div className="space-y-2">
        <Label>
          Is the customer currently insured with Progressive Commercial Auto?
        </Label>
        <Controller
          control={control}
          name="step1.insuredWithProgressiveCommercialAuto"
          render={({ field }) => (
            <Select
              value={field.value === 'na' ? undefined : field.value ?? undefined}
              onValueChange={(v) => {
                field.onChange(v);
              }}
              onOpenChange={() => {
                field.onChange(field.value);
              }}
            >
              <SelectTrigger
                className={"w-full"}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Choose</SelectLabel>
                  {YES_NO.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        <FieldError
          name="step1.insuredWithProgressiveCommercialAuto"
          step="step1"
          control={control}
        />
      </div>}

      {/* Hazmat toggle */}
      <div className="px-3">
        <div className="w-full my-4">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step1.hazmatPlacard"
              render={({ field }) => (
                <Switch
                  id="hazmat-toggle"
                  checked={!!field.value}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                    // validate immediately so error banner shows
                    methods.trigger("step1.hazmatPlacard");
                  }}
                />
              )}
            />
            <Label htmlFor="hazmat-toggle" className="cursor-pointer">
              Do any listed vehicles or the load require a hazardous material placard?
            </Label>
          </div>

          {/* Blocking error banner */}
          {(hazmat || methods.formState.errors?.step1?.hazmatPlacard) && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 text-red-700 p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{String(methods?.formState?.errors?.step1?.hazmatPlacard?.message ?? "We cannot cover any hazardous materials at this time for carriers except Accelerant.")}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="px-3">
        <div className="w-full my-4">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step1.haulDoubleTripleTrailers"
              render={({ field }) => (
                <Switch
                  id="haulDoubleTripleTrailers-toggle"
                  checked={!!field.value}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                    // validate immediately so error banner shows
                    methods.trigger("step1.haulDoubleTripleTrailers");
                  }}
                />
              )}
            />
            <Label htmlFor="haulDoubleTripleTrailers-toggle" className="cursor-pointer">
              Do you haul double/triple trailers?
            </Label>
          </div>

          {/* Blocking error banner */}
          {(haulDoubleTripleTrailers || methods.formState.errors?.step1?.haulDoubleTripleTrailers) && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 text-red-700 p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{String(methods?.formState?.errors?.step1?.haulDoubleTripleTrailers?.message ?? "We cannot cover any double/triple trailers at this time.")}</span>
            </div>
          )}
        </div>
      </div>

      {carrier?.includes('Nirvana') && <div className="space-y-2">
        <Label>
          Has the Insured operated under this DOT # for 2 or more FULL years?
        </Label>
        <Controller
          control={control}
          name="step1.operatedTwoYears"
          render={({ field }) => (
            <Select
              value={field.value === 'na' ? undefined : field.value ?? undefined}
              onValueChange={(v) => {
                field.onChange(v);
              }}
              onOpenChange={() => {
                field.onChange(field.value);
              }}
            >
              <SelectTrigger
                className={"w-full"}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Choose</SelectLabel>
                  {YES_NO.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        <FieldError
          name="step1.operatedTwoYears"
          step="step1"
          control={control}
        />
      </div>}

      {/* Personal use (optional, mirrors your Step6 new code) */}
      <div className={`px-3 ${blockedCls}`}>
        <div className="w-1/3 my-4">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step1.personalUseOfTrailer"
              render={({ field }) => (
                <Switch
                  id="tr-personal-use"
                  checked={!!field.value}
                  onCheckedChange={(v) => {
                    if (v) {
                      personalTrailerSetterRef.current = field.onChange;
                      setOpenPersonalTrailerWarn(true);
                    } else {
                      field.onChange(false);
                    }
                  }}
                />
              )}
            />
            <Label htmlFor="tr-personal-use" className="cursor-pointer">
              Will there be any personal use of any trailers(s)?
            </Label>
          </div>
        </div>
      </div>
      {/* Personal-use warning */}
      <Dialog open={openPersonalTrailerWarn} onOpenChange={setOpenPersonalTrailerWarn}>
        <DialogContent className="sm:max-w-md">
          <div className="flex justify-center mb-2">
            <div className="rounded-full bg-red-50 p-3 text-red-600"><AlertCircle className="h-6 w-6" /></div>
          </div>
          <DialogHeader>
            <DialogTitle className="text-center">Warning</DialogTitle>
            <p className="text-center text-base">
              By selecting personal use of trailers, this quote would automatically be declined.
            </p>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button variant="outline" onClick={() => { personalTrailerSetterRef.current(true); setOpenPersonalTrailerWarn(false); }}>
              I Understand
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { personalTrailerSetterRef.current(false); setOpenPersonalTrailerWarn(false); }}>
              De-select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className={`px-3 ${blockedCls}`}>
        <div className="w-1/3 my-4">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step1.personalUseOfVehicle"
              render={({ field }) => (
                <Switch
                  id="personal-use"
                  checked={!!field.value}
                  onCheckedChange={(v) => {
                    if (v) {
                      personalVehicleSetterRef.current = field.onChange;
                      setOpenPersonalVehicleWarn(true);
                    } else {
                      field.onChange(false);
                    }
                  }}
                />
              )}
            />
            <Label htmlFor="personal-use" className="cursor-pointer">
              Will there be any personal use of any vehicle(s)?
            </Label>
          </div>
        </div>
      </div>

      {/* Personal-use warning */}
      <Dialog open={openPersonalVehicleWarn} onOpenChange={setOpenPersonalVehicleWarn}>
        <DialogContent className="sm:max-w-md">
          <div className="flex justify-center mb-2">
            <div className="rounded-full bg-red-50 p-3 text-red-600"><AlertCircle className="h-6 w-6" /></div>
          </div>
          <DialogHeader>
            <DialogTitle className="text-center">Warning</DialogTitle>
            <p className="text-center text-base">
              By selecting personal use of vehicles, this quote would automatically be declined.
            </p>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button variant="outline" onClick={() => { personalVehicleSetterRef.current(true); setOpenPersonalVehicleWarn(false); }}>
              I Understand
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { personalVehicleSetterRef.current(false); setOpenPersonalVehicleWarn(false); }}>
              De-select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
